/**
 * Micro Map Component
 * 
 * Canvas-based tactical view showing detailed hex grid (flat view only)
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { TerrainMap, AxialCoord, TerrainType } from '../types/terrain';
import { Player as PlayerType } from '../types/player';
import { hexSpiral, pixelToHex, isInBounds, hexToPixel, hexNeighbors, hexDistance, hexCorners } from '../utils/hexMath';
import { drawHexSync, drawHighlightedHex, fillBackground, clearCanvas, drawRouteOverlay, drawRouteLine } from './HexRenderer';
import { preloadImages, getHexTilePath } from '../utils/imageLoader';
import { useZoom } from '../hooks/useZoom';
import { usePan } from '../hooks/usePan';
import { useHexInteraction } from '../hooks/useHexInteraction';
import { useTouchGestures } from '../hooks/useTouchGestures';
import { ZoomSlider } from './ZoomSlider';
import { PlayerStatsOverlay } from './PlayerStatsOverlay';
import { usePlayer } from '../contexts/PlayerContext';
import { drawSegmentedPlayerMarker, PlayerMarker, PLAYER_COLORS } from '../utils/playerMarkerRenderer';
import { getMarkerImage } from '../utils/playerMarkers';

interface MicroMapProps {
  map: TerrainMap;
  center: AxialCoord;
  onCenterChange?: (center: AxialCoord) => void;
  hexSize?: number;
  initialZoom?: number;
  hasUnresolvedMonster?: boolean;
  isInCombat?: boolean;
  onZoomChange?: (zoom: number) => void; // Added to notify parent of zoom changes
  onPlayerMarkerClick?: () => void; // Called when player marker is clicked
  onDistantHexClick?: (coord: AxialCoord) => void; // Called when non-adjacent hex is clicked
  pendingDestination?: AxialCoord | null;
  onAnimationComplete?: () => void;
  isStoryReady?: boolean;
  plannedRoute?: AxialCoord[];
  nextQuestHex?: { q: number; r: number } | null; // Next quest starting location (red highlight)
  playerData?: PlayerType; // Player object for accessing selectedMarkerIcon
}

export const MicroMap: React.FC<MicroMapProps> = ({
  map,
  center,
  onCenterChange,
  hexSize = 30,
  initialZoom,
  hasUnresolvedMonster,
  isInCombat = false,
  onZoomChange,
  onPlayerMarkerClick,
  onDistantHexClick,
  pendingDestination,
  onAnimationComplete,
  nextQuestHex = null,
  isStoryReady = false,
  plannedRoute,
  playerData,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredHex, setHoveredHex] = useState<AxialCoord | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const renderPendingRef = useRef(false);
  
  // Visual position for animation (starts at current position, animates to destination)
  const [visualPosition, setVisualPosition] = useState<AxialCoord | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationStartTimeRef = useRef<number | null>(null);
  const animationDuration = 1000; // 1 second animation
  
  // Use PlayerContext for player state
  const { player, position: playerPosition, movePlayer, exploredHexes } = usePlayer();
  
  // Store callback in ref to avoid dependency issues
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  useEffect(() => {
    onAnimationCompleteRef.current = onAnimationComplete;
  }, [onAnimationComplete]);
  
  // Track all values that trigger renders via refs
  const renderStateRef = useRef({
    offset: { x: 0, y: 0 },
    center: center,
    hoveredHex: null as AxialCoord | null,
    playerPosition: playerPosition,
    playerStamina: player.stamina,
    zoom: 1,
    map: map,
    hexSize: hexSize,
    canvasSize: { width: 800, height: 600 },
    exploredHexes: exploredHexes,
    imagesLoaded: false,
    plannedRoute: plannedRoute,
  });
  
  // Initialize renderStateRef with current values on mount
  useEffect(() => {
    renderStateRef.current.center = center;
    renderStateRef.current.playerPosition = playerPosition;
    renderStateRef.current.playerStamina = player.stamina;
    renderStateRef.current.map = map;
    renderStateRef.current.hexSize = hexSize;
    renderStateRef.current.exploredHexes = exploredHexes;
  }, []); // Only on mount
  
  // Use extracted hooks for zoom and pan (removed isometric)
  const {
    zoom,
    MIN_ZOOM,
    MAX_ZOOM,
    setZoom,
    handleWheel: handleZoomWheel,
    currentZoomRef,
  } = useZoom({
    minZoom: 0.1, // Allow zooming out to see entire island
    maxZoom: 5, // Reduced from 10 since no isometric mode
    initialZoom: initialZoom ?? 1,
  });

  const {
    offset,
    isDragging,
    hasDragged,
    handleMouseDown: handlePanMouseDown,
    handleMouseMove: handlePanMouseMove,
    handleMouseUp: handlePanMouseUp,
    handleMouseLeave: handlePanMouseLeave,
    handleWheel: handlePanWheel,
    resetOffset,
  } = usePan({
    center,
    panSpeed: 1.5,
  });
  
  // Initialize offsetRef after offset is available
  const offsetRef = useRef(offset);
  
  // Track touch-based panning offset
  const touchPanOffsetRef = useRef({ x: 0, y: 0 });
  
  // Touch gestures for pinch-to-zoom and touch panning
  const {
    isTouching,
  } = useTouchGestures({
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    currentZoom: zoom,
    onZoomChange: setZoom,
    elementRef: canvasRef,
    onPanStart: () => {
      // Store current offset when touch pan starts
      touchPanOffsetRef.current = { ...offset };
    },
    onPanMove: (deltaX: number, deltaY: number) => {
      // Update offset based on touch pan
      touchPanOffsetRef.current.x += deltaX;
      touchPanOffsetRef.current.y += deltaY;
      // Trigger a state update by modifying the offset through the pan hook
      // We'll simulate this by creating a synthetic mouse move event
    },
    onPanEnd: () => {
      // Touch pan ended
    },
  });
  
  const { handleClick: handleHexClick } = useHexInteraction({
    map,
    center,
    zoom,
    isDragging,
    hasDragged,
    offset,
    canvasSize,
    hexSize,
    onCenterChange,
    setZoom,
    hasUnresolvedMonster,
    isInCombat,
    onPlayerMarkerClick,
    onDistantHexClick,
  });
  
  const playerImageRef = useRef<HTMLImageElement | null>(null);
  const playerPositionRef = useRef<AxialCoord>(playerPosition);
  
  // Initialize and sync visual position to current position (when not animating)
  useEffect(() => {
    if (!isAnimating) {
      // When not animating, always sync to player position
      // This ensures the marker renders correctly after animation completes
      if (visualPosition === null || 
          (visualPosition.q !== playerPosition.q || visualPosition.r !== playerPosition.r)) {
        setVisualPosition(playerPosition);
      }
    }
  }, [playerPosition, isAnimating, visualPosition]);
  
  // Handle animation cancellation (monster encounter mid-route)
  useEffect(() => {
    // If animation is running but pendingDestination was cleared (RESET), stop animation immediately
    if (isAnimating && !pendingDestination) {
      console.log('🛑 Animation cancelled (monster encounter), stopping and syncing to playerPosition');
      setIsAnimating(false);
      animationStartTimeRef.current = null;
      setVisualPosition(playerPosition);
    }
  }, [isAnimating, pendingDestination, playerPosition]);
  
  // Trigger animation when story is ready and destination is pending
  useEffect(() => {
    console.log('🎬 Animation trigger check (full map):', {
      isStoryReady,
      hasPendingDestination: !!pendingDestination,
      pendingDest: pendingDestination,
      isAnimating,
      visualPosition,
      playerPosition
    });
    
    if (isStoryReady && pendingDestination && !isAnimating && visualPosition) {
      // Check if we're actually moving (visual position != destination)
      if (visualPosition.q !== pendingDestination.q || visualPosition.r !== pendingDestination.r) {
        console.log('✅ Starting player marker animation (full map)');
        setIsAnimating(true);
        animationStartTimeRef.current = Date.now();
      } else {
        console.log('⚠️ Already at destination, completing immediately (full map)');
        // Already at destination, just complete
        if (onAnimationCompleteRef.current) {
          onAnimationCompleteRef.current();
        }
      }
    }
  }, [isStoryReady, pendingDestination, isAnimating, visualPosition, playerPosition]);
  
  // Helper function to interpolate position along a route
  const interpolateAlongRoute = useCallback((route: AxialCoord[] | undefined, progress: number, fallbackStart: AxialCoord, fallbackEnd: AxialCoord): AxialCoord => {
    // If no route or route is too short, use straight line
    if (!route || route.length < 2) {
      return {
        q: fallbackStart.q + (fallbackEnd.q - fallbackStart.q) * progress,
        r: fallbackStart.r + (fallbackEnd.r - fallbackStart.r) * progress
      };
    }
    
    // Calculate which segment we're on
    const totalSegments = route.length - 1;
    const segmentProgress = progress * totalSegments;
    const currentSegmentIndex = Math.floor(segmentProgress);
    const segmentLocalProgress = segmentProgress - currentSegmentIndex;
    
    // Clamp to valid segment
    const segmentIndex = Math.min(currentSegmentIndex, totalSegments - 1);
    const segmentStart = route[segmentIndex];
    const segmentEnd = route[segmentIndex + 1];
    
    // Interpolate within the current segment
    return {
      q: segmentStart.q + (segmentEnd.q - segmentStart.q) * segmentLocalProgress,
      r: segmentStart.r + (segmentEnd.r - segmentStart.r) * segmentLocalProgress
    };
  }, []);

  // Animation loop
  useEffect(() => {
    if (!isAnimating || !pendingDestination || !visualPosition) return;
    
    const startPosition = { ...visualPosition }; // Capture start position
    const endPosition = { ...pendingDestination };
    
    const animate = () => {
      if (!animationStartTimeRef.current) return;
      
      const elapsed = Date.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      // Ease-in-out function
      const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const easedProgress = easeInOut(progress);
      
      // Interpolate position along route (or straight line if no route)
      const newPosition = interpolateAlongRoute(plannedRoute, easedProgress, startPosition, endPosition);
      
      setVisualPosition(newPosition);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - batch state updates
        setVisualPosition(endPosition);
        // Use setTimeout to batch the state update and callback
        setTimeout(() => {
          setIsAnimating(false);
          animationStartTimeRef.current = null;
          if (onAnimationCompleteRef.current) {
            onAnimationCompleteRef.current();
          }
        }, 0);
      }
    };
    
    const frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [isAnimating, pendingDestination, onAnimationComplete, plannedRoute, interpolateAlongRoute]); // Added plannedRoute and interpolateAlongRoute
  
  // Keep playerPositionRef in sync
  useEffect(() => {
    playerPositionRef.current = playerPosition;
  }, [playerPosition.q, playerPosition.r]);
  
  // Sync ref with state
  useEffect(() => {
    currentZoomRef.current = zoom;
  }, [zoom]);
  
  // Notify parent of zoom changes
  useEffect(() => {
    if (onZoomChange) {
      onZoomChange(zoom);
    }
  }, [zoom, onZoomChange]);

  // Auto-center on player and set 50% zoom when component first mounts
  useEffect(() => {
    // Center on player position
    if (onCenterChange) {
      onCenterChange(playerPosition);
    }
    
    // Set zoom to 50% of the range (same logic as ZoomSlider handleCenterClick)
    const zoomRange = MAX_ZOOM - MIN_ZOOM;
    const targetZoom = MIN_ZOOM + (zoomRange * 0.5); // 50% of the range
    setZoom(targetZoom);
    
    // Reset pan offset to ensure proper centering
    resetOffset();
  }, []); // Empty dependency array - only run once on mount

  // Load player marker image based on selected marker
  useEffect(() => {
    const img = new Image();
    const markerImage = getMarkerImage(playerData?.selectedMarkerIcon);
    img.src = markerImage;
    img.onload = () => {
      playerImageRef.current = img;
    };
  }, [playerData?.selectedMarkerIcon]);
  
  // Update canvas size to match container
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);
        if (width > 0 && height > 0) {
          setCanvasSize({ width, height });
        }
      }
    };
    
    updateCanvasSize();
    
    const resizeObserver = new ResizeObserver(updateCanvasSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, []);
  
  // Dynamic view radius based on zoom level
  const viewRadius = Math.max(3, Math.floor(20 / zoom));

  // Reset offset when center changes
  useEffect(() => {
    resetOffset();
  }, [center.q, center.r, resetOffset]);

  // Sync renderStateRef with all state changes
  useEffect(() => {
    renderStateRef.current.offset = offset;
    offsetRef.current = offset;
  }, [offset]);
  
  useEffect(() => {
    renderStateRef.current.center = center;
  }, [center.q, center.r]);
  
  useEffect(() => {
    renderStateRef.current.hoveredHex = hoveredHex;
  }, [hoveredHex?.q, hoveredHex?.r]);
  
  useEffect(() => {
    renderStateRef.current.playerPosition = playerPosition;
    playerPositionRef.current = playerPosition;
  }, [playerPosition.q, playerPosition.r]);
  
  useEffect(() => {
    renderStateRef.current.playerStamina = player.stamina;
  }, [player.stamina]);
  
  useEffect(() => {
    renderStateRef.current.zoom = zoom;
    currentZoomRef.current = zoom;
  }, [zoom]);
  
  useEffect(() => {
    renderStateRef.current.map = map;
  }, [map]);
  
  useEffect(() => {
    renderStateRef.current.hexSize = hexSize;
  }, [hexSize]);
  
  useEffect(() => {
    renderStateRef.current.canvasSize = canvasSize;
  }, [canvasSize.width, canvasSize.height]);
  
  useEffect(() => {
    renderStateRef.current.exploredHexes = exploredHexes;
  }, [exploredHexes]);
  
  useEffect(() => {
    renderStateRef.current.plannedRoute = plannedRoute;
  }, [plannedRoute]);

  // Store render function in ref to prevent recreation
  const renderRef = useRef<(() => void) | undefined>(undefined);
  
  // Render the map - uses refs for all dynamic values
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Get all values from renderStateRef
    const state = renderStateRef.current;
    const currentZoom = currentZoomRef.current;
    const currentOffset = state.offset;
    const currentCenter = state.center;
    const currentHoveredHex = state.hoveredHex;
    const currentPlayerPosition = state.playerPosition;
    const currentMap = state.map;
    const currentHexSize = state.hexSize;
    const currentCanvasSize = state.canvasSize;
    const currentExploredHexes = state.exploredHexes;
    const currentPlayerStamina = state.playerStamina;
    const currentPlannedRoute = state.plannedRoute;
    
    if (!currentMap) return;
    
    // Clear and fill background
    clearCanvas(ctx, currentCanvasSize.width, currentCanvasSize.height);
    fillBackground(ctx, currentCanvasSize.width, currentCanvasSize.height, '#1a3a5a');

    // Save context state
    ctx.save();

    // Calculate the center hex position in pixels
    const centerPixel = hexToPixel(currentCenter, currentHexSize);
    
    // Translate so the center hex appears in the middle of the canvas
    ctx.translate(currentCanvasSize.width / 2, currentCanvasSize.height / 2);
    
    // Apply zoom
    ctx.scale(currentZoom, currentZoom);
    
    // Apply pan offset and center translation
    const panX = currentOffset.x / currentZoom;
    const panY = currentOffset.y / currentZoom;
    ctx.translate(
      -centerPixel.x + panX,
      -centerPixel.y + panY
    );

    // Get hexes to render (entire map)
    const hexesToRender = Array.from(currentMap.hexes.values()).map(hex => ({ q: hex.q, r: hex.r }));
    
    // Render each hex (skip ocean hexes - they're just background)
    let landHexCount = 0;
    for (const coord of hexesToRender) {
      if (!isInBounds(coord, currentMap.width, currentMap.height)) continue;

      const hex = currentMap.hexes.get(`${coord.q},${coord.r}`);
      if (!hex) continue;

      // Skip rendering ocean hexes - they're just the background
      if (hex.terrain === TerrainType.Ocean) continue;

      landHexCount++;
      
      // Draw flat hex
      drawHexSync(ctx, coord, hex.terrain, currentHexSize, true);
    }

    // Render fog of war on unexplored land hexes
    let exploredCount = 0;
    let fogCount = 0;
    for (const coord of hexesToRender) {
      if (!isInBounds(coord, currentMap.width, currentMap.height)) continue;
      
      const hex = currentMap.hexes.get(`${coord.q},${coord.r}`);
      if (!hex) continue;
      
      // Skip fog of war for ocean hexes
      if (hex.terrain === TerrainType.Ocean) continue;
      
      // Check if hex is explored
      const hexKey = `${coord.q},${coord.r}`;
      const isExplored = currentExploredHexes.has(hexKey);
      
      if (isExplored) {
        exploredCount++;
      }
      
      if (!isExplored) {
        fogCount++;
        // Check if adjacent to any explored hex (creates a "frontier" effect)
        const neighbors = hexNeighbors(coord);
        const hasExploredNeighbor = neighbors.some(n => 
          currentExploredHexes.has(`${n.q},${n.r}`)
        );
        
        // Fog of war - darker for unexplored, lighter for frontier
        const fogAlpha = hasExploredNeighbor ? 0.4 : 0.7;
        const fogColor = hasExploredNeighbor ? '#1a1a1a' : '#000000';
        
        // Draw fog overlay
        drawHighlightedHex(ctx, coord, currentHexSize, fogColor, fogAlpha);
      }
    }

    // Draw planned route (if exists)
    if (currentPlannedRoute && currentPlannedRoute.length > 1) {
      drawRouteOverlay(ctx, currentPlannedRoute, currentHexSize, false);
      drawRouteLine(ctx, currentPlannedRoute, currentHexSize, false);
    }

    // Highlight adjacent hexes as valid move targets (if player has stamina)
    if (currentPlayerStamina > 0 && !currentPlannedRoute) {
      const adjacentHexes = hexNeighbors(currentPlayerPosition);
      for (const adjHex of adjacentHexes) {
        if (!isInBounds(adjHex, currentMap.width, currentMap.height)) continue;
        
        // Skip ocean hexes - can't move to ocean
        const adjHexData = currentMap.hexes.get(`${adjHex.q},${adjHex.r}`);
        if (adjHexData && adjHexData.terrain === TerrainType.Ocean) continue;
        
        // Green highlight for valid moves
        drawHighlightedHex(ctx, adjHex, currentHexSize, '#00ff00', 0.15);
      }
    }

    // Highlight next quest hex with pulsing red
    if (nextQuestHex && isInBounds(nextQuestHex, currentMap.width, currentMap.height)) {
      // Pulsing animation (1-second cycle)
      const pulsePhase = (Date.now() % 1000) / 1000; // 0 to 1
      const pulseAlpha = 0.5 + 0.3 * Math.sin(pulsePhase * Math.PI * 2); // 0.5 to 0.8
      drawHighlightedHex(ctx, nextQuestHex, currentHexSize, '#ff3333', pulseAlpha);
    }

    // Highlight hovered hex
    if (currentHoveredHex && isInBounds(currentHoveredHex, currentMap.width, currentMap.height)) {
      const distanceToPlayer = hexDistance(currentHoveredHex, currentPlayerPosition);
      const isValidMove = distanceToPlayer === 1 && currentPlayerStamina > 0;
      
      // Use green for valid moves, cyan for other hexes
      const hoverColor = isValidMove ? '#00ff00' : '#00ffff';
      const hoverAlpha = isValidMove ? 0.4 : 0.2;
      
      drawHighlightedHex(ctx, currentHoveredHex, currentHexSize, hoverColor, hoverAlpha);
    }

    // Draw destination hex highlight (if pending - show during travel)
    if (pendingDestination) {
      drawHighlightedHex(ctx, pendingDestination, currentHexSize, '#ffff00', 0.3);
    }
    
    // Draw player marker at visual position (for animation)
    const markerPosition = visualPosition || currentPlayerPosition;
    if (playerImageRef.current) {
      // Get hex corners for proper hexagonal positioning
      const corners = hexCorners(markerPosition, currentHexSize);
      
      // TODO: Replace with actual multiplayer data when multiplayer is implemented
      // For now, create mock data for testing segmented markers
      // This demonstrates 1-4 players based on hex coordinates (for testing purposes)
      const mockPlayerCount = 1; // Change this to 1, 2, or 3 to test different segments
      const playersOnHex: PlayerMarker[] = Array.from({ length: mockPlayerCount }, (_, i) => ({
        playerId: `player-${i}`,
        color: PLAYER_COLORS[i],
      }));
      
      // Draw segmented player marker (supports 1-4 players)
      drawSegmentedPlayerMarker(ctx, corners, playersOnHex, playerImageRef.current);
    }

    // Restore context
    ctx.restore();
  }, [visualPosition, pendingDestination, nextQuestHex]);
  
  // Store render function in ref
  useEffect(() => {
    renderRef.current = render;
  }, [render]);

  // Unified render loop
  useEffect(() => {
    let frameId: number | null = null;
    let lastRenderTime = 0;
    const minRenderInterval = 16; // ~60fps max
    
    // Track previous state to detect changes
    let lastState = {
      offset: { ...renderStateRef.current.offset },
      center: { ...renderStateRef.current.center },
      hoveredHex: renderStateRef.current.hoveredHex ? { ...renderStateRef.current.hoveredHex } : null,
      playerPosition: { ...renderStateRef.current.playerPosition },
      playerStamina: renderStateRef.current.playerStamina,
      zoom: renderStateRef.current.zoom,
      map: renderStateRef.current.map,
      hexSize: renderStateRef.current.hexSize,
      canvasSize: { ...renderStateRef.current.canvasSize },
      imagesLoaded: renderStateRef.current.imagesLoaded,
      plannedRoute: renderStateRef.current.plannedRoute,
      nextQuestHex: nextQuestHex ? { ...nextQuestHex } : null,
    };
    
    const renderLoop = () => {
      const now = performance.now();
      const state = renderStateRef.current;
      
      // Check if any tracked values have changed
      const hasChanged = 
        state.offset.x !== lastState.offset.x ||
        state.offset.y !== lastState.offset.y ||
        state.center.q !== lastState.center.q ||
        state.center.r !== lastState.center.r ||
        (state.hoveredHex?.q !== lastState.hoveredHex?.q) ||
        (state.hoveredHex?.r !== lastState.hoveredHex?.r) ||
        state.playerPosition.q !== lastState.playerPosition.q ||
        state.playerPosition.r !== lastState.playerPosition.r ||
        state.playerStamina !== lastState.playerStamina ||
        Math.abs(state.zoom - lastState.zoom) > 0.001 ||
        state.map !== lastState.map ||
        state.hexSize !== lastState.hexSize ||
        state.canvasSize.width !== lastState.canvasSize.width ||
        state.canvasSize.height !== lastState.canvasSize.height ||
        state.imagesLoaded !== lastState.imagesLoaded ||
        state.plannedRoute !== lastState.plannedRoute ||
        (nextQuestHex?.q !== lastState.nextQuestHex?.q) ||
        (nextQuestHex?.r !== lastState.nextQuestHex?.r);
      
      // Render if changed and enough time has passed
      if (hasChanged && (now - lastRenderTime >= minRenderInterval)) {
        if (!renderPendingRef.current) {
          renderPendingRef.current = true;
          requestAnimationFrame(() => {
            if (renderRef.current) {
              renderRef.current();
            }
            renderPendingRef.current = false;
            lastRenderTime = now;
            
            // Update lastState after render
            lastState = {
              offset: { ...state.offset },
              center: { ...state.center },
              hoveredHex: state.hoveredHex ? { ...state.hoveredHex } : null,
              playerPosition: { ...state.playerPosition },
              playerStamina: state.playerStamina,
              zoom: state.zoom,
              map: state.map,
              hexSize: state.hexSize,
              canvasSize: { ...state.canvasSize },
              imagesLoaded: state.imagesLoaded,
              plannedRoute: state.plannedRoute,
              nextQuestHex: nextQuestHex ? { ...nextQuestHex } : null,
            };
          });
        }
      }
      
      frameId = requestAnimationFrame(renderLoop);
    };
    
    frameId = requestAnimationFrame(renderLoop);
    
    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);

  // Preload images for visible hexes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const hexesToRender = hexSpiral(center, viewRadius);
    const imagePaths: string[] = [];

    for (const coord of hexesToRender) {
      if (!isInBounds(coord, map.width, map.height)) continue;
      const hex = map.hexes.get(`${coord.q},${coord.r}`);
      if (!hex) continue;
      
      const imagePath = getHexTilePath(hex.terrain, coord.q, coord.r);
      imagePaths.push(imagePath);
    }

    let loadedCount = 0;
    const totalImages = imagePaths.length;
    let lastFlagValue = renderStateRef.current.imagesLoaded;

    const loadImages = async () => {
      for (const path of imagePaths) {
        try {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = path;
          });
          
          loadedCount++;
          if (loadedCount % 3 === 0 || loadedCount === totalImages) {
            renderStateRef.current.imagesLoaded = !lastFlagValue;
            lastFlagValue = renderStateRef.current.imagesLoaded;
          }
        } catch (error) {
          // Image failed to load - will use color fallback
        }
      }
    };

    loadImages();
  }, [map, center, viewRadius]);

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    handlePanMouseDown(e);
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Update hovered hex
    const rect = canvas.getBoundingClientRect();
    const centerPixel = hexToPixel(center, hexSize);
    const panX = offset.x / zoom;
    const panY = offset.y / zoom;
    const x = (e.clientX - rect.left - canvasSize.width / 2) / zoom - panX + centerPixel.x;
    const y = (e.clientY - rect.top - canvasSize.height / 2) / zoom - panY + centerPixel.y;
    const hex = pixelToHex({ x, y }, hexSize);
    
    // Only set hovered hex if it's adjacent or is the player's hex (and not ocean)
    if (isInBounds(hex, map.width, map.height)) {
      const hexData = map.hexes.get(`${hex.q},${hex.r}`);
      if (hexData && hexData.terrain === TerrainType.Ocean) {
        setHoveredHex(null);
        handlePanMouseMove(e);
        return;
      }
      
      const distance = hexDistance(hex, playerPosition);
      const isAdjacent = distance === 1;
      const isPlayerHex = hex.q === playerPosition.q && hex.r === playerPosition.r;

      if (isAdjacent || isPlayerHex) {
        setHoveredHex(hex);
      } else {
        setHoveredHex(null);
      }
    } else {
      setHoveredHex(null);
    }

    handlePanMouseMove(e);
  };

  // Hex click handler
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    handleHexClick(e, canvasRef);
  };

  // Register wheel event with { passive: false } to allow preventDefault()
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      // Allow browser zoom (Ctrl/Cmd + scroll) to work normally
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      
      e.preventDefault();
      
      if (e.shiftKey) {
        // ZOOM MODE (Shift + Scroll)
        const zoomFactor = 0.02;
        const delta = e.deltaY > 0 ? 1 - zoomFactor : 1 + zoomFactor;
        let newZoom = currentZoomRef.current * delta;
        
        // Clamp to valid range
        newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

        currentZoomRef.current = newZoom;
        setZoom(newZoom);
      } else {
        // PAN MODE (Scroll without Shift)
        // Create a synthetic React event for the pan handler
        const syntheticEvent = {
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          preventDefault: () => {},
        } as React.WheelEvent<HTMLCanvasElement>;
        handlePanWheel(syntheticEvent);
      }
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [MIN_ZOOM, MAX_ZOOM, setZoom, currentZoomRef, handlePanWheel]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handlePanMouseUp}
        onMouseLeave={handlePanMouseLeave}
        onClick={handleClick}
        style={{
          width: '100%',
          height: '100%',
          border: '2px solid #666',
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: '#2a2a2a',
          display: 'block',
          touchAction: 'none', // Prevent default touch behaviors like browser pinch-zoom
        }}
      />
      {/* Player Stats Overlay */}
      <PlayerStatsOverlay />
      
      {/* Zoom Slider with Center Button */}
      <ZoomSlider 
        zoom={zoom}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        onZoomChange={setZoom}
        playerPosition={playerPosition}
        onCenterChange={onCenterChange}
        style={{
          position: 'absolute',
          right: 15,
          bottom: 15,
        }}
      />
    </div>
  );
};
